/* global Engage, jQuery, Elm */

/**
 * @function initializeLocatorSearchResults
 * @description Initializes the core search results module for the Engage Locator.
 * @param  {jQuery} $ - A reference to the global jQuery namespace.
 */
void (function initializeLocatorSearchResults($) {
	'use strict';

	window.Engage = window.Engage || {};
	Engage.Locator = Engage.Locator || {};

	if (!Engage.Locator.Messaging) {
		const retryDelay = 20;
		setTimeout(() => initializeLocatorSearchResults($), retryDelay);
		return;
	}

	/**
	 * @private
	 * @const {number} defaultScrollOffest - Defines the default scroll offset for the scroll animation.
	 * @memberof Window.Engage.Locator.SearchResults
	 */

	const defaultScrollOffest = -50;

	/**
	 * @function getScrollOffsetInPx
	 * @memberof Window.Engage.Locator
	 * @description Gets the scroll offset in pixels to apply to the scroll animation when scrolling the page.
	 * @returns {number} - A number in pixels that represents the offset to be applied when scrolling tha page.
	 */
	Engage.Locator.getScrollOffsetInPx = () => defaultScrollOffest;

	/**
	 * @namespace Window.Engage.Locator.SearchResults
	 */
	Engage.Locator.SearchResults = Engage.Locator.SearchResults || {};

	/**
	 * @function initialize
	 * @memberof Window.Engage.Locator.SearchResults
	 * @description Initializes and embeds the Engage Locator Search Results module.
	 * @example
	 * Engage.Locator.SearchResults.initialize(
	 *      <%: this.ModuleId %>,
	 *      document.getElementById('<%: this.LocatorSearchResults.ClientID%>'));
	 * @param  {number} moduleId - The module Id of the search module.
	 * @param  {element} element - The document element to embed the ELM search application into.
	 */
	Engage.Locator.SearchResults.initialize = (moduleId, element) => {
		const messaging = Engage.Locator.Messaging;
		const servicesFramework = $.ServicesFramework(moduleId); // eslint-disable-line new-cap
		const tabId = servicesFramework.getTabId();
		const antiForgeryValue = servicesFramework.getAntiForgeryValue();
		const serviceRoot = servicesFramework.getServiceRoot('Engage/Locator');
		const options = {
			baseUrl: `${serviceRoot}Locations/`,
			headers: {
				ModuleId: moduleId.toString(),
				TabId: tabId,
				RequestVerificationToken: antiForgeryValue,
			},
			localization: Engage.Locator.localResources,
			attributeDefinitions: Engage.Locator.AttributeDefinitions || [],
			queryString: Engage.Locator.QueryString || [],
			showNearMe:
				window.location.protocol === 'https:' &&
				Boolean(navigator && navigator.geolocation) &&
				Boolean(Engage.Locator.Settings.ShowNearMeButton),
			settings: Engage.Locator.Settings,
			uniqueId: `${moduleId}-${tabId}`,
			regions: Engage.Locator.regions || [],
			countries: Engage.Locator.countries || [],
			initialAddress: Engage.Locator.initialAddress,
			addLocationUrl: Engage.Locator.addLocationUrl,
		};
		const elmApp = Elm.Engage.SearchResults.Main.init({
			node: element,
			flags: options,
		});

		/**
		 * @private
		 * @function scrollToMapOrResults
		 * @description Scroll the page using the offset from {Window.Engage.Locator.getScrollOffsetInPx}.
		 */
		function scrollToMapOrResults() {
			const $map = $('.nfda-locator__map');
			const $searchResults = $('.nfda-locator__search-results');
			if ($searchResults.length === 0) {
				return;
			}

			let scrollTo = $searchResults.offset().top;
			if ($map.length !== 0) {
				scrollTo = $map.offset().top;
			}

			let offset = defaultScrollOffest;
			if (
				Engage.Locator.getScrollOffsetInPx &&
				Engage.Locator.getScrollOffsetInPx.constructor &&
				Engage.Locator.getScrollOffsetInPx.call &&
				Engage.Locator.getScrollOffsetInPx.apply
			) {
				const radix = 10;
				const thirdPartyOffset = parseInt(
					Engage.Locator.getScrollOffsetInPx(),
					radix,
				);
				if (!isNaN(thirdPartyOffset)) {
					offset = thirdPartyOffset;
				}
			}

			const scrollTop = scrollTo + offset;
			if (isNaN(scrollTop)) {
				return;
			}

			$('html, body').animate({ scrollTop }, 'fast');
		}

		/**
		 * @global
		 * @description Defines the structure of the callback for all outgoing port message from elm.
		 * @callback elmPortCallback
		 * @param {Object} payload - The encoded JSON payload sent from ELM.
		 */

		/**
		 * @private
		 * @name elmOutboundPortCallback
		 * @typedef elmPortCallback
		 * @description Handles all outgoing messages from the Engage Locator Search Results ELM application.
		 * @memberof Window.Engage.Locator.SearchResults
		 * @param {Object} payload - The JSON payload sent from ELM.
		 */
		elmApp.ports.out.subscribe((payload) => {
			if (!payload || !payload.type) {
				return;
			}

			switch (payload.type.toUpperCase()) {
				case 'SENDNEWLOCATIONS':
					messaging.publish(
						messaging.messageTypes.newLocations,
						payload.data,
					);
					break;
				case 'TRACKEVENT':
					messaging.publish(
						messaging.messageTypes.trackEvent,
						payload.data,
					);
					break;
				case 'SHOWLOADINGREQUEST':
					messaging.publish(messaging.messageTypes.showLoading);
					break;
				case 'SENDSCROLLREQUEST':
					scrollToMapOrResults();
					break;
				case 'ADDLOCATION':
					window.Engage.Locator.EditLocation.init(
						Engage.Locator.tabId,
						Engage.Locator.moduleId,
						null,
					);
					break;
				default:
					return;
			}
		});

		/**
		 * @private
		 * @name locationUpdateSubscriptionCallback
		 * @typedef subscriptionCallback
		 * @description Handles all {Window.Engage.Locator.Messaging.messageTypes.locationUpdate} messages.
		 * @memberof Window.Engage.Locator.SearchResults
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(
			messaging.messageTypes.locationUpdate,
			(messageType, { latitude, longitude }) => {
				if (
					messageType !== messaging.messageTypes.locationUpdate ||
					!latitude ||
					!longitude
				) {
					return;
				}

				elmApp.ports.sub.send({
					type: 'LocationUpdateResult',
					data: { latitude, longitude },
				});
			},
		);

		/**
		 * @private
		 * @name resetSearchFormSubscriptionCallback
		 * @typedef subscriptionCallback
		 * @description Handles all {Window.Engage.Locator.Messaging.messageTypes.resetSearchFrom} messages.
		 * @memberof Window.Engage.Locator.SearchResults
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(
			messaging.messageTypes.resetSearchFrom,
			(messageType) => {
				if (messageType !== messaging.messageTypes.resetSearchFrom) {
					return;
				}

				elmApp.ports.sub.send({
					type: 'ResetSearch',
				});
			},
		);

		/**
		 * @private
		 * @name newQueryFiltersSubscriptionCallback
		 * @typedef subscriptionCallback
		 * @description Handles all {Window.Engage.Locator.Messaging.messageTypes.newQueryFilters} messages.
		 * @memberof Window.Engage.Locator.SearchResults
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(
			messaging.messageTypes.newQueryFilters,
			(messageType, { filters }) => {
				if (
					messageType !== messaging.messageTypes.newQueryFilters ||
					!filters
				) {
					return;
				}

				elmApp.ports.sub.send({
					type: 'NewQueryFiltersResult',
					data: filters,
				});
			},
		);

		/**
		 * @private
		 * @name boundingBoxUpdateSubscriptionCallback
		 * @typedef subscriptionCallback
		 * @description Handles all {Window.Engage.Locator.Messaging.messageTypes.boundingBoxUpdate} messages.
		 * @memberof Window.Engage.Locator.SearchResults
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(
			messaging.messageTypes.boundingBoxUpdate,
			(messageType, data) => {
				if (
					messageType !== messaging.messageTypes.boundingBoxUpdate ||
					!data
				) {
					return;
				}

				elmApp.ports.sub.send({
					type: 'BoundingBoxResult',
					data,
				});
			},
		);

		/**
		 * @private
		 * @const {string[]} messagesThatTriggerSearch - An array of all messages types that mark a search
		 * action has been completed.
		 * @memberof Window.Engage.Locator.SearchResults
		 */
		const messagesThatTriggerSearch = [
			messaging.messageTypes.validateZipCode,
			messaging.messageTypes.locationUpdate,
			messaging.messageTypes.newQueryFilters,
			messaging.messageTypes.resetSearchFrom,
			messaging.messageTypes.boundingBoxUpdate,
			messaging.messageTypes.showLoading,
		];

		/**
		 * @private
		 * @name messagesThatTriggerSearchSubscriptionCallback
		 * @typedef subscriptionCallback
		 * @description Subscribe to all messages in the Engage Locator, only handle messages
		 * that exist in the {messagesThatTriggerSearch} member.
		 * @memberof Window.Engage.Locator.SearchResults
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(messaging.locatorMessagePrefix, (messageType) => {
			if (messagesThatTriggerSearch.indexOf(messageType) === -1) {
				return;
			}

			elmApp.ports.sub.send({
				type: 'PerformSearchResult',
			});
		});

		/**
		 * @private
		 * @const {string[]} messagesThatMarkSearchAsComplete - An array of all messages types that mark a search
		 * action has been completed.
		 * @memberof Window.Engage.Locator.SearchResults
		 */
		const messagesThatMarkSearchAsComplete = [
			messaging.messageTypes.zipCodeValidationResult,
			messaging.messageTypes.newLocations,
		];

		/**
		 * @private
		 * @name SubscriptionCallback
		 * @typedef messagesThatMarkSearchAsCompleteSubscriptionCallback
		 * @description Subscribe to all messages in the Engage Locator, only handle messages
		 * that exist in the {messagesThatMarkSearchAsComplete} member.
		 * @memberof Window.Engage.Locator.SearchResults
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(messaging.locatorMessagePrefix, (messageType) => {
			if (messagesThatMarkSearchAsComplete.indexOf(messageType) === -1) {
				return;
			}

			elmApp.ports.sub.send({
				type: 'SearchCompletedResult',
			});
		});

		messaging.subscribe(
			messaging.messageTypes.refreshLocation,
			(messageType, { locationKey }) => {
				if (
					messageType !== messaging.messageTypes.refreshLocation ||
					!locationKey
				) {
					return;
				}

				elmApp.ports.sub.send({
					type: 'RefreshLocation',
					data: { locationKey },
				});
			},
		);
	};
})(jQuery);
